import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { api } from '../api/client';
import AchievementsPanel from '../components/AchievementsPanel';
import PhaserAvatar from '../components/PhaserAvatar';

const TAB_STYLE = (active) => ({
  fontWeight: active ? 700 : 400,
  color: active ? 'var(--accent)' : 'var(--text-secondary)',
  background: 'none',
  border: 'none',
  borderBottom: active ? '2px solid var(--accent)' : '2px solid transparent',
  cursor: 'pointer',
  transition: 'all 0.2s',
  whiteSpace: 'nowrap',
});

const STATUS_COLORS = {
  pending: 'badge-uncommon',
  completed: 'badge-success',
  cancelled: 'badge-redeemed',
  on_tick: 'badge-legendary',
};

const PURCHASES_DEFAULT = 15;
const ACTIVITY_DEFAULT = 30;

export default function ProfilePage() {
  const { user } = useAuth();
  const [tab, setTab] = useState('purchases');
  const tabContentRef = useRef(null);

  const handleTabChange = (newTab) => {
    setTab(newTab);
    setTimeout(() => tabContentRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }), 10);
  };
  const [orders, setOrders] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [pointHistory, setPointHistory] = useState([]);
  const [allLevels, setAllLevels] = useState([]);
  const [showAllPurchases, setShowAllPurchases] = useState(false);
  const [showAllActivity, setShowAllActivity] = useState(false);
  const [loadout, setLoadout] = useState(null);

  useEffect(() => {
    api.get('/bookings').then(setOrders).catch(() => {});
    api.get('/coupons').then(setCoupons).catch(() => {});
    api.get('/points/history').then(setPointHistory).catch(() => {});
    api.get('/users/levels').then(setAllLevels).catch(() => {});
    fetch('/api/avatar/loadout').then(r => r.json()).then(d => setLoadout(d)).catch(() => {});
  }, []);

  const userLevel = user?.level_data?.level || 1;

  // Build combined activity feed (points + coupons), sorted by date
  const activityFeed = [
    ...pointHistory.map(tx => ({
      id: `pts-${tx.id}`,
      reason: tx.reason,
      detail: tx.amount === 0 ? '+1 spin' : `${tx.amount > 0 ? '+' : ''}${tx.amount} pts`,
      color: tx.amount === 0 ? '#d6bcfa' : tx.amount > 0 ? 'var(--success)' : 'var(--danger)',
      date: tx.created_at,
    })),
    ...coupons.map(c => ({
      id: `cpn-${c.id}`,
      reason: `Coupon: ${c.reward_detail || c.reward_type}`,
      detail: c.redeemed_at ? 'Redeemed' : 'Active',
      color: c.redeemed_at ? 'var(--text-muted)' : 'var(--warning)',
      date: c.created_at,
    })),
  ].sort((a, b) => new Date(b.date) - new Date(a.date));

  const visibleActivity = showAllActivity ? activityFeed : activityFeed.slice(0, ACTIVITY_DEFAULT);
  const visibleOrders = showAllPurchases ? orders : orders.slice(0, PURCHASES_DEFAULT);

  return (
    <div>
      {/* Identity Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 20 }}>
        <div style={{ boxShadow: '0 0 16px rgba(56,161,105,0.35)', borderRadius: 8, overflow: 'hidden' }}>
          <PhaserAvatar
            avatarConfig={loadout
              ? { base_model: loadout.base_model || 'base_default', top: loadout.top, bottom: loadout.bottom, accessory: loadout.accessory, handheld: loadout.handheld }
              : { base_model: 'base_default' }}
            size={52}
            animated
          />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--text-primary)', lineHeight: 1.2 }}>
            {user?.username}
          </div>
          {user?.level_data && (
            <div style={{ fontSize: 13, color: 'var(--accent)', fontStyle: 'italic', marginTop: 2 }}>
              Lv.{user.level_data.level} · {user.level_data.title}
            </div>
          )}
        </div>
        <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
          <span style={{
            background: 'rgba(236,201,75,0.15)', border: '1px solid rgba(236,201,75,0.35)',
            borderRadius: 20, padding: '4px 10px', fontSize: 12, fontWeight: 700,
            color: 'var(--warning)',
          }}>
            {(user?.points || 0).toLocaleString()} pts
          </span>
          <span style={{
            background: 'rgba(159,122,234,0.15)', border: '1px solid rgba(159,122,234,0.35)',
            borderRadius: 20, padding: '4px 10px', fontSize: 12, fontWeight: 700,
            color: '#b794f4',
          }}>
            {user?.spin_tokens || 0} 🎰
          </span>
        </div>
      </div>

      {/* Level & XP Progress */}
      {user?.level_data && (
        <div className="card" style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{
                width: 44, height: 44, borderRadius: '50%',
                background: 'linear-gradient(135deg, var(--accent), #2f855a)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 18, fontWeight: 800, color: '#fff',
                boxShadow: '0 0 12px rgba(56,161,105,0.4)',
              }}>
                {user.level_data.level}
              </div>
              <div>
                <div style={{ fontSize: 16, fontWeight: 700 }}>Level {user.level_data.level}</div>
                <div style={{ fontSize: 13, color: 'var(--accent)', fontStyle: 'italic' }}>{user.level_data.title}</div>
              </div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--accent)' }}>{(user.level_data.xp || 0).toLocaleString()} XP</div>
            </div>
          </div>
          <div style={{
            width: '100%', height: 8, borderRadius: 4,
            background: 'var(--bg-secondary)', overflow: 'hidden',
          }}>
            <div style={{
              width: `${Math.round(user.level_data.progress * 100)}%`,
              height: '100%', borderRadius: 4,
              background: 'linear-gradient(90deg, var(--accent), #68d391)',
              transition: 'width 0.5s ease',
            }} />
          </div>
          {user.level_data.next_reward && (
            <div style={{
              marginTop: 10, padding: '8px 12px', borderRadius: 'var(--radius-sm)',
              background: user.level_data.next_reward.type === 'coupon' ? 'rgba(236,201,75,0.1)' : user.level_data.next_reward.type === 'spins' ? 'rgba(159,122,234,0.1)' : 'rgba(72,187,120,0.1)',
              border: `1px solid ${user.level_data.next_reward.type === 'coupon' ? 'rgba(236,201,75,0.3)' : user.level_data.next_reward.type === 'spins' ? 'rgba(159,122,234,0.3)' : 'rgba(72,187,120,0.2)'}`,
              display: 'flex', alignItems: 'center', gap: 8, fontSize: 13,
            }}>
              <span>{user.level_data.next_reward.type === 'coupon' ? '🎁' : user.level_data.next_reward.type === 'spins' ? '🎀' : '⭐'}</span>
              <span style={{ color: 'var(--text-secondary)' }}>
                <strong style={{ color: user.level_data.next_reward.type === 'coupon' ? '#ecc94b' : user.level_data.next_reward.type === 'spins' ? '#b794f4' : 'var(--accent)' }}>
                  Level {user.level_data.next_reward.level}
                </strong>
                {' — '}
                {user.level_data.next_reward.detail}
              </span>
            </div>
          )}
        </div>
      )}

      {/* Account Info */}
      <div className="card" style={{ marginBottom: 20, padding: '14px 16px' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 13, color: 'var(--text-muted)' }}>
          <div>Username: <strong style={{ color: 'var(--text-primary)' }}>{user?.username}</strong></div>
          <div>Member since: <strong style={{ color: 'var(--text-primary)' }}>{user?.created_at ? new Date(user.created_at).toLocaleDateString() : '-'}</strong></div>
          {user?.signed_up_with && (
            <div>Invite code: <strong style={{ color: 'var(--accent)', fontFamily: 'monospace', letterSpacing: 1 }}>{user.signed_up_with}</strong></div>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div style={{
        display: 'flex', gap: 0, borderBottom: '1px solid var(--border)',
        marginBottom: 16, overflowX: 'auto',
      }}>
        <button className="profile-tab" style={TAB_STYLE(tab === 'purchases')} onClick={() => handleTabChange('purchases')}>
          Purchases
        </button>
        <button className="profile-tab" style={TAB_STYLE(tab === 'activity')} onClick={() => handleTabChange('activity')}>
          Activity
        </button>
        <button className="profile-tab" style={TAB_STYLE(tab === 'coupons')} onClick={() => handleTabChange('coupons')}>
          Nugs
        </button>
        <button className="profile-tab" style={TAB_STYLE(tab === 'levels')} onClick={() => handleTabChange('levels')}>
          Levels
        </button>
        <button className="profile-tab" style={TAB_STYLE(tab === 'achievements')} onClick={() => handleTabChange('achievements')}>
          Achievements
        </button>
      </div>

      {/* Tab Content */}
      <div ref={tabContentRef}>

      {/* Purchases Tab */}
      {tab === 'purchases' && (
        <div className="card">
          <h3 style={{ marginBottom: 12 }}>Order History</h3>
          {orders.length === 0 ? (
            <div className="empty-state">No orders yet. Place your first pickup!</div>
          ) : (
            <>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ fontSize: 13 }}>
                  <thead>
                    <tr>
                      <th>Strain</th>
                      <th>Weight</th>
                      <th>Status</th>
                      <th>Pickup</th>
                      <th className="hide-mobile">Ordered</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleOrders.map(o => (
                      <tr key={o.id}>
                        <td><strong>{o.strain_name || '-'}</strong></td>
                        <td>{o.amount}</td>
                        <td>
                          <span className={`badge ${STATUS_COLORS[o.status] || ''}`}>
                            {o.status === 'on_tick' ? 'On Tick' : o.status}
                          </span>
                        </td>
                        <td style={{ fontSize: 12 }}>
                          {new Date(o.pickup_time).toLocaleString('en-GB', {
                            day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit'
                          })}
                        </td>
                        <td className="hide-mobile" style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                          {new Date(o.created_at).toLocaleDateString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {orders.length > PURCHASES_DEFAULT && (
                <button
                  onClick={() => setShowAllPurchases(v => !v)}
                  style={{
                    marginTop: 12, background: 'none', border: '1px solid var(--border)',
                    borderRadius: 'var(--radius-sm)', padding: '6px 14px',
                    fontSize: 12, color: 'var(--text-secondary)', cursor: 'pointer', width: '100%',
                  }}
                >
                  {showAllPurchases ? 'Show less' : `Show all ${orders.length} orders`}
                </button>
              )}
            </>
          )}
        </div>
      )}

      {/* Activity Tab */}
      {tab === 'activity' && (
        <div className="card">
          <h3 style={{ marginBottom: 12 }}>Activity</h3>
          {activityFeed.length === 0 ? (
            <div className="empty-state">No activity yet.</div>
          ) : (
            <>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ fontSize: 13 }}>
                  <thead>
                    <tr>
                      <th>Activity</th>
                      <th>Detail</th>
                      <th>When</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleActivity.map(tx => {
                      const d = new Date(tx.date);
                      const now = new Date();
                      const isToday = d.toDateString() === now.toDateString();
                      const yesterday = new Date(now); yesterday.setDate(now.getDate() - 1);
                      const isYesterday = d.toDateString() === yesterday.toDateString();
                      const h = d.getHours(), m = d.getMinutes();
                      const hr = h % 12 || 12;
                      const ap = h < 12 ? 'am' : 'pm';
                      const time = m === 0 ? `${hr}${ap}` : `${hr}:${String(m).padStart(2, '0')}${ap}`;
                      const MON = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
                      const dateLabel = isToday ? `Today` : isYesterday ? `Yesterday` : `${d.getDate()} ${MON[d.getMonth()]}`;
                      return (
                      <tr key={tx.id}>
                        <td>{tx.reason}</td>
                        <td style={{ color: tx.color }}>{tx.detail}</td>
                        <td style={{ fontSize: 12, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                          <span style={{ display: 'block' }}>{dateLabel}</span>
                          <span style={{ color: 'var(--text-muted)', opacity: 0.7 }}>{time}</span>
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {activityFeed.length > ACTIVITY_DEFAULT && (
                <button
                  onClick={() => setShowAllActivity(v => !v)}
                  style={{
                    marginTop: 12, background: 'none', border: '1px solid var(--border)',
                    borderRadius: 'var(--radius-sm)', padding: '6px 14px',
                    fontSize: 12, color: 'var(--text-secondary)', cursor: 'pointer', width: '100%',
                  }}
                >
                  {showAllActivity ? 'Show less' : `Show all ${activityFeed.length} entries`}
                </button>
              )}
            </>
          )}
        </div>
      )}

      {/* Coupons Tab */}
      {tab === 'coupons' && (() => {
        const now = new Date();
        const isActive = c => !c.redeemed_at && (!c.expires_at || new Date(c.expires_at) > now);
        const isExpired = c => !c.redeemed_at && c.expires_at && new Date(c.expires_at) <= now;
        const sorted = [...coupons].sort((a, b) => {
          const aActive = isActive(a) ? 0 : isExpired(a) ? 1 : 2;
          const bActive = isActive(b) ? 0 : isExpired(b) ? 1 : 2;
          if (aActive !== bActive) return aActive - bActive;
          return new Date(b.created_at) - new Date(a.created_at);
        });
        const sourceLabel = t => ({ admin_gift: 'Gift', spin_prize: 'Spin', level_milestone: 'Level Reward', points_redemption: 'Shop' }[t] || t);
        return (
          <div className="card">
            <h3 style={{ marginBottom: 12 }}>Your Reward Nugs</h3>
            {sorted.length === 0 ? (
              <div className="empty-state">No nugs yet. Spin the leaf or hit the Stash Shop!</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {sorted.map(c => {
                  const active = isActive(c);
                  const expired = isExpired(c);
                  return (
                    <div key={c.id} style={{
                      padding: '12px 14px', borderRadius: 'var(--radius-sm)',
                      background: 'var(--bg-secondary)',
                      border: `1px solid ${active ? 'rgba(236,201,75,0.4)' : 'transparent'}`,
                      opacity: active ? 1 : 0.55,
                      display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
                    }}>
                      <div style={{ fontSize: 22, lineHeight: 1 }}>🎁</div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 3 }}>
                          <span style={{ fontFamily: 'monospace', fontSize: 13, color: active ? 'var(--warning)' : 'var(--text-muted)', fontWeight: 700 }}>
                            {c.coupon_code}
                          </span>
                          <span className={`badge ${c.redeemed_at ? 'badge-redeemed' : expired ? 'badge-redeemed' : 'badge-active'}`} style={{ fontSize: 10 }}>
                            {c.redeemed_at ? 'Claimed' : expired ? 'Expired' : 'Active'}
                          </span>
                          <span style={{ fontSize: 10, color: 'var(--text-muted)', background: 'var(--bg-card)', padding: '1px 6px', borderRadius: 4 }}>
                            {sourceLabel(c.reward_type)}
                          </span>
                        </div>
                        <div style={{ fontSize: 13, color: 'var(--text-primary)', fontWeight: 600 }}>
                          {c.reward_detail || c.reward_type}
                        </div>
                        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
                          {c.redeemed_at
                            ? `Claimed ${new Date(c.redeemed_at).toLocaleDateString()}`
                            : c.expires_at
                            ? `${expired ? 'Expired' : 'Expires'} ${new Date(c.expires_at).toLocaleDateString()}`
                            : `Issued ${new Date(c.created_at).toLocaleDateString()}`}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })()}

      {/* Levels Tab */}
      {tab === 'levels' && (
        <div className="card">
          <h3 style={{ marginBottom: 12 }}>All Levels</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {allLevels.map(lv => {
              const isCurrent = lv.level === userLevel;
              const isUnlocked = lv.level <= userLevel;
              const isCoupon = lv.reward_type === 'coupon';
              const isSpins = lv.reward_type === 'spins';
              return (
                <div key={lv.level} style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '10px 12px', borderRadius: 'var(--radius-sm)',
                  background: isCurrent ? 'rgba(56,161,105,0.15)' : 'var(--bg-secondary)',
                  border: isCurrent ? '2px solid var(--accent)' : isCoupon ? '1px solid rgba(236,201,75,0.3)' : isSpins ? '1px solid rgba(159,122,234,0.3)' : '1px solid transparent',
                  opacity: isUnlocked ? 1 : 0.5,
                }}>
                  <div style={{
                    width: 34, height: 34, borderRadius: '50%', flexShrink: 0,
                    background: isCurrent
                      ? 'linear-gradient(135deg, var(--accent), #2f855a)'
                      : isUnlocked
                        ? 'linear-gradient(135deg, #2f855a, #276749)'
                        : 'var(--bg-card)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 13, fontWeight: 800,
                    color: isUnlocked ? '#fff' : 'var(--text-muted)',
                    boxShadow: isCurrent ? '0 0 10px rgba(56,161,105,0.5)' : 'none',
                  }}>
                    {lv.level}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                      fontSize: 14, fontWeight: isCurrent ? 700 : 600,
                      color: isCurrent ? 'var(--accent)' : isUnlocked ? 'var(--text-primary)' : 'var(--text-muted)',
                    }}>
                      {lv.title}
                      {isCurrent && <span style={{ fontSize: 11, marginLeft: 6, color: 'var(--accent)' }}>(YOU)</span>}
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 1 }}>
                      {isCoupon ? (
                        <span style={{ color: '#ecc94b', fontWeight: 600 }}>🎁 {lv.coupon_detail}</span>
                      ) : isSpins ? (
                        <span style={{ color: '#b794f4', fontWeight: 600 }}>🎀 +{lv.spin_value} Spins</span>
                      ) : lv.points_value ? (
                        <span style={{ color: 'var(--success)' }}>⭐ +{lv.points_value} Bud Points</span>
                      ) : (
                        <span>Starting level</span>
                      )}
                    </div>
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', textAlign: 'right', flexShrink: 0 }}>
                    {lv.xp_required > 0 ? `${lv.xp_required.toLocaleString()} XP` : ''}
                  </div>
                  {isUnlocked && lv.level > 1 && (
                    <span style={{ fontSize: 14, color: 'var(--success)', flexShrink: 0 }}>✓</span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Achievements Tab */}
      {tab === 'achievements' && (
        <AchievementsPanel />
      )}

      </div>
    </div>
  );
}
